import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock3,
  Info,
  Loader2,
  ShieldAlert,
  UserRound,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatHumanVerifyTimestamp,
  humanVerifyCategoryLabel,
  humanVerifyMachineLabel,
  humanVerifyMachineClasses as machineClasses,
  humanVerifySeverityClasses as severityClasses,
  humanVerifyStatusClasses as statusClasses,
  type HumanVerifyItem,
  type HumanVerifyItemStatus,
  type HumanVerifySeverity,
} from '@/lib/humanVerify';

export type HumanVerifyResolution = Extract<HumanVerifyItemStatus, 'verified' | 'dismissed'>;

export interface HumanVerifyItemRowProps {
  item: HumanVerifyItem;
  onResolve: (item: HumanVerifyItem, status: HumanVerifyResolution, note: string) => void;
  resolving?: boolean;
  compact?: boolean;
  className?: string;
}

function severityIcon(severity: HumanVerifySeverity) {
  if (severity === 'info') return <Info className="size-3" />;
  if (severity === 'block') return <ShieldAlert className="size-3" />;
  return <AlertTriangle className="size-3" />;
}

function Chip({
  children,
  className,
}: {
  children: ReactNode;
  className: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold', className)}>
      {children}
    </span>
  );
}

export function HumanVerifyItemRow({
  item,
  onResolve,
  resolving = false,
  compact = false,
  className,
}: HumanVerifyItemRowProps) {
  const [note, setNote] = useState(item.note ?? '');
  const verifiedAt = formatHumanVerifyTimestamp(item.verified_at);
  const resolved = item.status !== 'needs_review';

  useEffect(() => {
    setNote(item.note ?? '');
  }, [item.id, item.note]);

  const resolve = (status: HumanVerifyResolution) => {
    onResolve(item, status, note.trim());
  };

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-card p-2.5',
        compact ? 'space-y-2' : 'space-y-3',
        className,
      )}
    >
      <div className={cn('grid gap-2', compact ? 'grid-cols-1' : 'grid-cols-[minmax(0,1fr)_minmax(180px,0.55fr)]')}>
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip className={severityClasses(item.severity)}>
              {severityIcon(item.severity)}
              {item.severity}
            </Chip>
            <Chip className={machineClasses(item.machine_status)}>
              <Bot className="size-3" />
              {humanVerifyMachineLabel(item.machine_status)}
            </Chip>
            {resolved && (
              <Chip className={statusClasses(item.status)}>
                {item.status === 'verified' ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
                {item.status}
              </Chip>
            )}
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {humanVerifyCategoryLabel(item.category)}
            </div>
            <p className="mt-0.5 text-[12.5px] font-medium leading-snug text-foreground">
              {item.prompt}
            </p>
          </div>
          {item.machine_context && (
            <p className="rounded-md border border-coral/20 bg-coral-light px-2 py-1.5 text-[11.5px] leading-snug text-coral-dark">
              {item.machine_context}
            </p>
          )}
        </div>

        <div className="min-w-0 space-y-2">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Note
            <textarea
              aria-label={`Note for ${item.prompt}`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className="mt-1 min-h-[60px] w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 text-[12px] font-normal normal-case leading-snug tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coral"
              placeholder="Add reviewer context"
            />
          </label>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => resolve('verified')}
              disabled={resolving}
              className="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-teal/30 bg-teal-light px-2 text-[11.5px] font-semibold text-teal-dark transition-colors hover:bg-teal/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resolving ? <Loader2 className="size-3 animate-spin" /> : <CheckCircle2 className="size-3" />}
              Verify
            </button>
            <button
              type="button"
              onClick={() => resolve('dismissed')}
              disabled={resolving}
              className="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-coral/30 bg-coral-light px-2 text-[11.5px] font-semibold text-coral-dark transition-colors hover:bg-coral/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resolving ? <Loader2 className="size-3 animate-spin" /> : <XCircle className="size-3" />}
              Dismiss
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border pt-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <UserRound className="size-3" />
          {item.verified_by || (resolved ? 'Reviewer not recorded' : 'Awaiting reviewer')}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock3 className="size-3" />
          {verifiedAt || (resolved ? 'Time not recorded' : 'Not reviewed yet')}
        </span>
      </div>
    </div>
  );
}
